import React, { useCallback, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Toolbar,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Menu as MenuIcon } from 'react-feather';
import { THEMES, includesAny } from 'src/constants';
import LogoThree from 'src/components/LogoThree';
import screenRoutes from 'src/config/screen.routes';
import { useDispatch, useSelector } from 'react-redux';
// import Search from './Search';
import { originators } from 'src/store/actions/originatorActions';
import loanProductService from 'src/services/loanProductsService';
import { useSnackbar } from 'notistack';
import { ProductContext } from 'src/context/ProductContext';
import { themePrimaryColor, themeSecondaryColor } from 'src/theme';
import { TopMenuOptionContext } from 'src/context/TopMenuOptionContext';
import Notifications from './Notifications';
import Settings from './Settings';
import Account from './Account';

const useStyles = makeStyles(theme => ({
  root: {
    ...theme.name === THEMES.ACELERA
      ? {
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.045)'
        }
      : {},
    ...theme.name === THEMES.ONE_DARK
      ? {
          backgroundColor: theme.palette.secondary.main
        }
      : {}
  },
  toolbar: {
    minHeight: 64,
    paddingRight: 24
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1)
  },
  title: {
    color: themeSecondaryColor
  },
  logo: {
    width: 232,
    marginLeft: 39
  },
  button: {
    textTransform: 'none'
  },
  buttonTwo: {
    textTransform: 'none',
    opacity: 1,
    color: '#556f7b'
  },
  buttonLeaf: {
    color: '#FFF',
    padding: '10px 8px',
    justifyContent: 'flex-start',
    textTransform: 'none',
    letterSpacing: 0,
    width: '100%',
    fontWeight: theme.typography.fontWeightRegular,
    '&.depth-0': {
      '& $title': {
        fontWeight: theme.typography.fontWeightMedium
      }
    }
  },
  item: {
    display: 'block',
    paddingTop: 0,
    paddingBottom: 0,
    color: '#FFF'
  },
  itemLeaf: {
    display: 'flex',
    paddingTop: 0,
    paddingBottom: 0,
    color: '#FFF'
  },
  titleProduct: {
    color: themePrimaryColor,
    fontWeight: 'bold'
  }
}));

const navTopOptions = [
  {
    title: 'dashboard',
    roleAccess: ['SuperUser', 'Admin', 'AccountingDept'],
    href: `${screenRoutes.management.dashboard}`
  },
  {
    title: 'searchLoans',
    roleAccess: [
      'SuperUser',
      'Admin',
      'LoanServicer',
      'CustomerService',
      'AccountingDept'
    ],
    href: `${screenRoutes.loans.list}`
  },
  {
    title: 'reports',
    roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
    href: `${screenRoutes.reports.list}`,
    items: [
      {
        title: 'activeLoans',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        href: `${screenRoutes.reports.activeLoans}`
      },
      {
        title: 'delinquentLoans',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        href: `${screenRoutes.reports.delinquentLoans}`
      },
      {
        title: 'defaultedLoansReport',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        href: `${screenRoutes.reports.defaultedLoansReport}`
      },
      {
        title: 'eQLoanListReport',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        href: `${screenRoutes.reports.eQLoanListReport}`
      },
      {
        title: 'watchList',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        href: `${screenRoutes.loanManagementFilters.management.list}`
      },
      {
        title: 'historicalDataTape',
        roleAccess: ['SuperUser', 'Admin', 'LoanServicer', 'CustomerService'],
        href: `${screenRoutes.reports.historicalDataTape}`
      },
      {
        title: 'collateralReport',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        href: `${screenRoutes.reports.collateralReport}`
      },
      {
        title: 'accountsReceivableAging',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        href: `${screenRoutes.reports.loansAccountReceivableAgingReport}`
      },

      /*
       * {
       *   title: 'plaidTransactions',
       *   roleAccess: [
       *     'SuperUser',
       *     'Admin',
       *     'LoanServicer',
       *     'CustomerService',
       *     'AccountingDept'
       *   ],
       *   href: `${screenRoutes.reports.plaidTransactions}`
       * },
       */

      /*
       * {
       *   title: 'insuranceReport',
       *   roleAccess: [
       *     'SuperUser',
       *     'Admin',
       *     'LoanServicer',
       *     'CustomerService',
       *     'AccountingDept',
       *   ],
       *   href: `${screenRoutes.reports.insuranceReport}`,
       * },
       */
      {
        title: 'loansReceivable',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        href: `${screenRoutes.reports.loansReceivableReport}`
      },
      {
        title: 'portfolioBalance',
        roleAccess: [
          'SuperUser',
          'Admin',
          'LoanServicer',
          'CustomerService',
          'AccountingDept'
        ],
        href: `${screenRoutes.reports.portfolioBalance}`
      }
    ]
  }
];

function TopBar({ className, onMobileNavOpen, ...rest }) {
  const { product, setProduct } = useContext(ProductContext);
  const { menuOption, setMenuOption } = useContext(TopMenuOptionContext);
  const { user } = useSelector(state => state.account);
  const originatorsList = useSelector(state => state.originator.originator);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const roles = user?.roles ?? [];
  const classes = useStyles();

  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [options, setOptions] = useState([]);
  const open = Boolean(anchorEl);

  const handleOpenUserMenu = event => {
    setOpenMenu(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setOpenMenu(false);
  };

  const handleChange = (event, newValue) => {
    setMenuOption({ value: newValue });
  };

  const handleClickListItem = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (event, description, menuId) => {
    setProduct({
      id: menuId,
      description
    });

    localStorage.setItem(
      'loanProduct',
      JSON.stringify({
        id: menuId,
        description
      })
    );

    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getProductsByOriginator = useCallback(originatorId => {
    loanProductService
      .GetLoanProductByOriginator(originatorId)
      .then(resp => {
        if (resp?.data?.length > 0) {
          const newOptions = resp.data;
          setOptions(newOptions);
          const productSelected = localStorage.getItem('loanProduct');
          let productSelectedJson = null;
          if (productSelected) {
            productSelectedJson = JSON.parse(productSelected);
          }
          setProduct(productSelectedJson ?? newOptions[0]);
        } else {
          setOptions([]);
          setProduct({
            id: '',
            description: ''
          });
        }
      })
      .catch(err => {
        if (err.message) {
          enqueueSnackbar(t(err.message.toString()), {
            variant: 'error'
          });
        } else {
          enqueueSnackbar(t(err), {
            variant: 'error'
          });
        }
      });
  }, []);

  useEffect(() => {
    if (originatorsList?.length === 0) {
      dispatch(originators());
    }
  }, []);

  useEffect(() => {
    if (originatorsList?.length > 0) {
      const defaultOriginator = originatorsList[0].id;
      getProductsByOriginator(defaultOriginator);
    }
  }, [originatorsList.length > 0]);

  useEffect(() => {
    setMenuOption({ value: 'dashboard' });
  }, []);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        className={clsx(classes.root, className)}
        component="nav"
        {...rest}
      >
        <Toolbar className={classes.toolbar}>
          <Box className={classes.logo} display="flex">
            <RouterLink
              onClick={() => setMenuOption({ value: 'dashboard' })}
              to="/app/management/dashboard"
            >
              <LogoThree />
            </RouterLink>
          </Box>
          <IconButton
            aria-label="open drawer"
            color="#7a7b74"
            edge="start"
            onClick={onMobileNavOpen}
            sx={{ ml: 1, display: { lg: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <div>
            <Button
              className={classes.button}
              color="secondary"
              onClick={handleClickListItem}
              textColor="secondary"
            >
              <span className={classes.title}>{product.description}</span>
              <ExpandMoreIcon color="inherit" size="small" />
            </Button>
            <Menu
              anchorEl={anchorEl}
              id="lock-menu"
              MenuListProps={{
                'aria-labelledby': 'lock-button',
                role: 'listbox'
              }}
              onClose={handleClose}
              open={open}
            >
              {options.map(option => (
                <MenuItem
                  key={`menu-option-${option.id}`}
                  onClick={event => handleMenuItemClick(event, option.description, option.id)}
                  selected={option.id === product.id}
                >
                  {option.description}
                </MenuItem>
              ))}
            </Menu>
          </div>

          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', sm: 'none', md: 'flex' }
            }}
          >
            <Tabs
              aria-label="nav tabs example"
              indicatorColor="secondary"
              onChange={handleChange}
              role="navigation"
              textColor="secondary"
              value={menuOption.value}
            >
              {navTopOptions.map(item => {
                const hasAccess = item.roleAccess
                  ? includesAny(item.roleAccess, roles)
                  : false;

                if (!hasAccess) return <></>;

                if (item?.items) {
                  return (
                    <>
                      <Tab
                        key={`nav-top-option-${item.title}`}
                        className={classes.buttonTwo}
                        label={t(item.title)}
                        onClick={handleOpenUserMenu}
                        // sx={{ color: '#556f7b' }}
                        value={item.title}
                      />
                      {item.items.map(subItem => (
                        <Menu
                          key={`nav-top-menu-${subItem.title}`}
                          anchorEl={openMenu}
                          anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                          }}
                          id="menu-appbar"
                          keepMounted
                          onClose={handleCloseUserMenu}
                          open={openMenu}
                          sx={{ mt: '45px' }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                          }}
                        >
                          {item?.items
                            && item.items.map(subItemTwo => (
                              <MenuItem
                                key={`nav-top-sub-option-${subItemTwo.title}`}
                                component={RouterLink}
                                onClick={handleCloseUserMenu}
                                to={subItemTwo.href}
                              >
                                <span className={classes.title}>
                                  {t(subItemTwo.title)}
                                </span>
                              </MenuItem>
                            ))}
                        </Menu>
                      ))}
                    </>
                  );
                }

                return (
                  <Tab
                    key={`nav-top-option-${item.title}`}
                    className={classes.button}
                    component={RouterLink}
                    label={t(item.title)}
                    to={item.href}
                    value={item.title}
                  />
                );
              })}
            </Tabs>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'none', sm: 'none', md: 'flex' },
              marginLeft: 2
            }}
          >
            <Typography className={classes.titleProduct}>
              {product.description}
            </Typography>
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'flex', sm: 'flex', md: 'none' },
              marginLeft: 2
            }}
          />
          {/* Hidding search bar */}
          {/* <Search /> */}
          <Notifications />
          <Settings />
          <Box ml={2}>
            <Account />
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}

TopBar.propTypes = {
  className: PropTypes.string,
  onMobileNavOpen: PropTypes.func
};

export default TopBar;
